import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { environment } from '@environments/environment';
import { AuthStore } from '@scl/core/domains/auth/services/auth.store';

const CLERK_TOKEN_RETRY = new HttpContextToken<boolean>(() => false);
const CLERK_RETRY_FAILED_MESSAGE = 'Your session expired. Please sign in again.';
const ACTIVE_PROFILE_KEY = 'scl_active_profile';
const ACTIVE_PROFILE_HEADER = 'X-Active-Profile';

export const clerkAuthInterceptor: HttpInterceptorFn = (request, next) => {
  if (!isApiRequest(request.url) || isProviderRequest(request.url)) {
    return next(request);
  }

  const authStore = inject(AuthStore);

  return from(authStore.getAuthorizationToken()).pipe(
    switchMap((accessToken) => {
      const headers: Record<string, string> = {};

      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const activeProfile = localStorage.getItem(ACTIVE_PROFILE_KEY);
      if (activeProfile) {
        headers[ACTIVE_PROFILE_HEADER] = activeProfile;
      }

      const authorizedRequest = Object.keys(headers).length > 0
        ? request.clone({ setHeaders: headers })
        : request;

      return next(authorizedRequest).pipe(
        catchError((error) => {
          if (!shouldRetryWithFreshClerkToken(error, request, authStore, accessToken)) {
            return throwError(() => error);
          }

          return from(authStore.getAuthorizationToken({ forceRefresh: true })).pipe(
            catchError(() => {
              triggerClerkSignInRedirect(authStore);
              return throwError(() => error);
            }),
            switchMap((freshToken) => {
              if (!freshToken || freshToken === accessToken) {
                triggerClerkSignInRedirect(authStore);
                return throwError(() => error);
              }

              return next(request.clone({
                context: request.context.set(CLERK_TOKEN_RETRY, true),
                setHeaders: {
                  Authorization: `Bearer ${freshToken}`,
                  ...(activeProfile ? { [ACTIVE_PROFILE_HEADER]: activeProfile } : {})
                }
              })).pipe(
                catchError((retryError) => {
                  if (shouldRedirectToSignInAfterRetryFailure(retryError, authStore)) {
                    triggerClerkSignInRedirect(authStore);
                  }

                  return throwError(() => retryError);
                })
              );
            })
          );
        })
      );
    })
  );
};

function shouldRetryWithFreshClerkToken(
  error: unknown,
  request: Parameters<HttpInterceptorFn>[0],
  authStore: AuthStore,
  accessToken: string | null
): error is HttpErrorResponse {
  return error instanceof HttpErrorResponse
    && error.status === 401
    && Boolean(accessToken)
    && authStore.isClerk()
    && !request.context.get(CLERK_TOKEN_RETRY);
}

function shouldRedirectToSignInAfterRetryFailure(error: unknown, authStore: AuthStore): error is HttpErrorResponse {
  return error instanceof HttpErrorResponse
    && error.status === 401
    && authStore.isClerk();
}

function triggerClerkSignInRedirect(authStore: AuthStore): void {
  void authStore.handleClerkAuthenticationFailure(CLERK_RETRY_FAILED_MESSAGE);
}

function isApiRequest(url: string): boolean {
  if ((environment.apiUrlBase && url.startsWith(environment.apiUrlBase))
    || (environment.authServerUrlBase && url.startsWith(environment.authServerUrlBase))) {
    return true;
  }

  return url.startsWith('/api/') || url.startsWith('api/');
}

function isProviderRequest(url: string): boolean {
  const normalizedUrl = environment.apiUrlBase && url.startsWith(environment.apiUrlBase)
    ? url.slice(environment.apiUrlBase.length)
    : url;

  return normalizedUrl === '/api/auth/provider'
    || normalizedUrl === 'api/auth/provider';
}